import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  CalendarClock, UserCheck, Save, Plus, Trash2, ChevronRight, Zap,
} from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { key: "mon", label: "Mon" }, { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" }, { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" }, { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function OnCallSchedulePage() {
  const { propertyId: propId } = useProperty();

  const [schedule, setSchedule] = useState(null);
  const [roster, setRoster] = useState([]);
  const [onCallNow, setOnCallNow] = useState([]);
  const [todayKey, setTodayKey] = useState("");
  const [grid, setGrid] = useState({ mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] });
  const [threshold, setThreshold] = useState(30);   // R118
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [overrides, setOverrides] = useState([]);
  const [newOverride, setNewOverride] = useState({ date: todayISO(), user_ids: [], note: "" });
  const [creatingOverride, setCreatingOverride] = useState(false);

  const rosterById = useMemo(() => Object.fromEntries(roster.map((r) => [r.id, r])), [roster]);

  const load = useCallback(async () => {
    if (!propId) return;
    try {
      const [{ data: s }, { data: o }] = await Promise.all([
        api.get("/admin/oncall/schedule", { params: { property_id: propId } }),
        api.get("/admin/oncall/overrides", { params: { property_id: propId } }),
      ]);
      setSchedule(s.schedule);
      setRoster(s.roster || []);
      setOnCallNow(s.on_call_now || []);
      setTodayKey(s.today_key);
      const g = s.schedule?.weekly_grid || {};
      setGrid({
        mon: g.mon || [], tue: g.tue || [], wed: g.wed || [], thu: g.thu || [],
        fri: g.fri || [], sat: g.sat || [], sun: g.sun || [],
      });
      setThreshold(s.schedule?.escalate_after_minutes || 30);   // R118
      setDirty(false);
      setOverrides(o.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load on-call schedule");
    }
  }, [propId]);
  useEffect(() => { load(); }, [load]);

  const toggle = (day, uid) => {
    setGrid((prev) => {
      const next = { ...prev };
      const cur = new Set(prev[day] || []);
      if (cur.has(uid)) cur.delete(uid);
      else cur.add(uid);
      next[day] = Array.from(cur);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    if (!propId) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/oncall/schedule", {
        property_id: propId,
        timezone: schedule?.timezone || "America/New_York",
        weekly_grid: grid,
        escalate_after_minutes: Number(threshold) || 30,   // R118
      });
      toast.success("Schedule saved");
      setOnCallNow(data.on_call_now || []);
      setDirty(false);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Save failed");
    } finally { setSaving(false); }
  };

  const addOverride = async () => {
    if (!newOverride.user_ids.length) {
      toast.error("Pick at least one tech for the override");
      return;
    }
    setCreatingOverride(true);
    try {
      await api.post("/admin/oncall/overrides", {
        property_id: propId,
        date: newOverride.date,
        user_ids: newOverride.user_ids,
        note: newOverride.note || undefined,
      });
      toast.success("Override saved");
      setNewOverride({ date: todayISO(), user_ids: [], note: "" });
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to save override");
    } finally { setCreatingOverride(false); }
  };

  const deleteOverride = async (oid) => {
    if (!confirm("Delete this override?")) return;
    try {
      await api.delete(`/admin/oncall/overrides/${oid}`);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to delete");
    }
  };

  const toggleOverrideUser = (uid) => {
    setNewOverride((prev) => {
      const cur = new Set(prev.user_ids);
      if (cur.has(uid)) cur.delete(uid); else cur.add(uid);
      return { ...prev, user_ids: Array.from(cur) };
    });
  };

  if (!propId) {
    return (
      <div className="p-6" data-testid="oncall-no-property">
        <PageHeader eyebrow="Operations" title="On-Call Rotation" />
        <div className="text-sm text-zinc-400">Pick a community from the header first.</div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="oncall-page">
      <PageHeader
        eyebrow="Operations"
        title="Maintenance On-Call"
        subtitle="          Set a weekly rotation and one-off overrides. New tickets auto-assign to whoever is on duty right now."
        actions={
          <Button onClick={save} disabled={!dirty || saving}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="oncall-save-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save schedule"}
          </Button>
        }
      />

      {/* Who's on now */}
      <Card className="p-5 mb-6 bg-gradient-to-br from-orange-500/10 via-white to-white border-orange-200"
            data-testid="oncall-now-banner">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-orange-500/15 flex items-center justify-center">
            <Zap className="h-5 w-5 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline text-orange-700">On call right now</div>
            {onCallNow.length === 0 ? (
              <div className="text-sm text-zinc-600 mt-0.5">
                Nobody is on duty — tickets will land unassigned. Pick someone below.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 mt-1" data-testid="oncall-now-list">
                {onCallNow.map((uid) => {
                  const u = rosterById[uid];
                  return (
                    <Badge key={uid} className="bg-orange-500 hover:bg-orange-500 text-white"
                           data-testid={`oncall-now-${uid}`}>
                      <UserCheck className="h-3 w-3 mr-1" />
                      {u?.name || uid}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Weekly grid */}
      <Card className="p-5 mb-6" data-testid="oncall-weekly-grid">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-indigo-500" />
            <div className="overline">Weekly rotation</div>
          </div>
          {/* R118 — Autoescalate threshold */}
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-1.5"
               data-testid="oncall-threshold-row">
            <Zap className="h-3.5 w-3.5 text-amber-600" />
            <span className="text-xs text-amber-800">Escalate after</span>
            <Input type="number" min={5} max={480} step={5}
                    value={threshold}
                    onChange={(e) => { setThreshold(e.target.value); setDirty(true); }}
                    className="h-7 w-16 text-xs px-2"
                    data-testid="oncall-threshold-input" />
            <span className="text-xs text-amber-800">min unaccepted → notify PM</span>
          </div>
        </div>
        {roster.length === 0 ? (
          <div className="text-sm text-zinc-500">
            No maintenance staff assigned to this property yet.{" "}
            <a href="/admin/users" className="text-orange-600 hover:underline">
              Add one first <ChevronRight className="h-3 w-3 inline" />
            </a>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-zinc-500 border-b">
                  <th className="text-left px-2 py-2 sticky left-0 bg-white">Tech</th>
                  {DAYS.map((d) => (
                    <th key={d.key}
                        className={"text-center px-2 py-2 " + (d.key === todayKey ? "text-orange-600 font-semibold" : "")}
                        data-testid={`oncall-col-${d.key}`}>
                      {d.label}
                      {d.key === todayKey && <div className="text-[9px] text-orange-500">today</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {roster.map((u) => (
                  <tr key={u.id} className="border-b last:border-0 hover:bg-zinc-50"
                      data-testid={`oncall-row-${u.id}`}>
                    <td className="px-2 py-2 sticky left-0 bg-white">
                      <div className="font-medium text-zinc-900">{u.name}</div>
                      <div className="text-[10px] text-zinc-500">{u.email} · {u.role}</div>
                    </td>
                    {DAYS.map((d) => {
                      const on = (grid[d.key] || []).includes(u.id);
                      return (
                        <td key={d.key} className="text-center px-1 py-1">
                          <button
                            type="button"
                            onClick={() => toggle(d.key, u.id)}
                            data-testid={`oncall-cell-${d.key}-${u.id}`}
                            className={
                              "h-7 w-7 rounded-md border transition " +
                              (on
                                ? "bg-orange-500 border-orange-500 text-white hover:bg-orange-600"
                                : "bg-white border-zinc-200 hover:border-orange-300")
                            }
                            aria-pressed={on}
                            title={on ? "Remove from " + d.label : "Add to " + d.label}
                          >
                            {on ? "✓" : ""}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {dirty && (
          <div className="text-xs text-amber-700 mt-3 flex items-center gap-1"
               data-testid="oncall-unsaved">
            Unsaved changes — click &quot;Save schedule&quot; to apply.
          </div>
        )}
      </Card>

      {/* Overrides */}
      <Card className="p-5" data-testid="oncall-overrides">
        <div className="flex items-center gap-2 mb-4">
          <CalendarClock className="h-4 w-4 text-emerald-500" />
          <div className="overline">One-off overrides</div>
        </div>
        <div className="text-xs text-zinc-500 mb-3">
          An override replaces the weekly rotation for a single day (e.g., swap coverage while a tech is out).
        </div>

        {/* Create override */}
        <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3 mb-4"
             data-testid="oncall-new-override">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={newOverride.date}
                     min={todayISO()}
                     onChange={(e) => setNewOverride({ ...newOverride, date: e.target.value })}
                     data-testid="oncall-override-date" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Who&apos;s on that day</Label>
              <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto rounded-md border p-2 bg-white">
                {roster.length === 0
                  ? <span className="text-xs text-zinc-500">Add maintenance staff first.</span>
                  : roster.map((u) => {
                      const on = newOverride.user_ids.includes(u.id);
                      return (
                        <button key={u.id} type="button"
                                onClick={() => toggleOverrideUser(u.id)}
                                data-testid={`oncall-override-pick-${u.id}`}
                                className={"text-xs px-2 py-1 rounded-full border transition " +
                                  (on ? "bg-emerald-500 border-emerald-500 text-white"
                                      : "bg-white border-zinc-200 hover:border-emerald-300")}>
                          {u.name}
                        </button>
                      );
                    })}
              </div>
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-xs">Note (optional)</Label>
            <Textarea value={newOverride.note}
                      onChange={(e) => setNewOverride({ ...newOverride, note: e.target.value })}
                      placeholder="Marco on vacation — Sara covers"
                      className="h-16" data-testid="oncall-override-note" />
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={addOverride}
                    disabled={creatingOverride || !newOverride.user_ids.length}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="oncall-override-save-btn">
              <Plus className="h-4 w-4 mr-1" />
              {creatingOverride ? "Saving…" : "Add override"}
            </Button>
          </div>
        </div>

        {/* Existing overrides */}
        {overrides.length === 0 ? (
          <div className="text-sm text-zinc-500 italic">No upcoming overrides.</div>
        ) : (
          <div className="space-y-2" data-testid="oncall-override-list">
            {overrides.map((o) => (
              <div key={o.id}
                   className="flex items-center gap-3 rounded border border-zinc-100 p-3"
                   data-testid={`oncall-override-${o.id}`}>
                <div className="text-sm font-medium text-zinc-900 w-28 shrink-0">{o.date}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-1">
                    {(o.user_ids || []).map((uid) => {
                      const u = rosterById[uid];
                      return (
                        <Badge key={uid} variant="outline" className="border-emerald-300 text-emerald-700">
                          {u?.name || uid}
                        </Badge>
                      );
                    })}
                  </div>
                  {o.note && <div className="text-xs text-zinc-500 mt-1">{o.note}</div>}
                </div>
                <button onClick={() => deleteOverride(o.id)}
                        title="Delete override"
                        className="p-1 rounded hover:bg-rose-50 text-zinc-400 hover:text-rose-600"
                        data-testid={`oncall-override-delete-${o.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
